import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getGuest, getGuestSlice } from "../../../services/getApi"; // تابع اصلی
import { DELGuest } from "services/deleteApi";

const options = {
  weekday: 'long',
  month: 'numeric',
  day: 'numeric',
  year: "numeric"
};

function AllG({ change, setLoading }) {
  const [loading1, setLoading1] = useState(true);
  const [guests, setGuests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const navigate = useNavigate();

  // صفحه‌بندی
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loaderRef = useRef(null);

  // تابع دریافت داده‌ها (همان getGuest با پارامتر page)
  const fetchGuests = useCallback(async (pageNum, reset = false) => {
    try {
      const res = await getGuestSlice(pageNum); 
      const newData = res.data || [];
      
      console.log(`Page ${pageNum} received:`, newData.length, "items");
      
      if (reset) {
        setGuests(newData);
      } else {
        setGuests(prev => [...prev, ...newData]);
      }
      
      // اگر کمتر از ۵ آیتم آمد، دیگر صفحه‌ای وجود ندارد
      if (newData.length < 5) {
        setHasMore(false);
      } else {
        setHasMore(true);
      }
      
      return newData;
    } catch (err) {
      console.error("Error fetching guests:", err);
      setHasMore(false);
      return [];
    }
  }, []);

  // بارگذاری اولیه
  useEffect(() => {
    const loadInitial = async () => {
      setLoading1(true);
      setLoading(true);
      setPage(1);
      setHasMore(true);
      setGuests([]);
      
      await fetchGuests(1, true);
      
      setLoading1(false);
      setLoading(false);
    };
    
    loadInitial();
  }, [change, fetchGuests, setLoading]);

  // بارگذاری صفحه بعدی
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || loading1) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    await fetchGuests(nextPage, false);
    setPage(nextPage);
    setIsLoadingMore(false);
  }, [isLoadingMore, hasMore, loading1, page, fetchGuests]);

  // تشخیص اسکرول با IntersectionObserver
  useEffect(() => {
    if (loading1 || !hasMore) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px 100px 0px" }
    );
    
    const currentLoader = loaderRef.current;
    if (currentLoader) {
      observer.observe(currentLoader);
    }
    
    return () => {
      if (currentLoader) {
        observer.unobserve(currentLoader);
      }
    };
  }, [loading1, hasMore, loadMore]);

  // آمارها
  const stats = {
    total: guests.length,
    special: guests.filter(g => g.isSpecial).length,
    today: guests.filter(g => {
      const today = new Date().toLocaleDateString('fa-IR');
      return new Date(g.createdAt).toLocaleDateString('fa-IR') === today;
    }).length,
    totalIncome: guests.reduce((sum, g) => sum + (g.price || 0), 0)
  };

  // فیلتر و جستجو
  const filteredGuests = guests.filter(guest => {
    const matchesSearch = 
      guest.fullname?.includes(searchTerm) ||
      guest.phone?.includes(searchTerm) ||
      guest.city?.includes(searchTerm) ||
      guest.number_room?.toString().includes(searchTerm);

    if (filterType === "all") return matchesSearch;
    if (filterType === "special") return matchesSearch && guest.isSpecial;
    if (filterType === "today") {
      const today = new Date().toLocaleDateString('fa-IR');
      return matchesSearch && new Date(guest.createdAt).toLocaleDateString('fa-IR') === today;
    }
    return matchesSearch;
  });

  return (
    <div className="p-4 page-ani">
      {/* Header - بدون تغییر */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-primary to-secondary rounded-xl flex items-center justify-center shadow-lg">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">لیست مهمان‌ها</h1>
            <p className="text-sm text-gray-400">مدیریت و مشاهده اطلاعات مهمان‌ها</p>
          </div>
        </div>
        <button 
          className="btn btn-primary gap-2"
          onClick={() => navigate("/new-guest/person")}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          افزودن مهمان
        </button>
      </div>

      {/* Stats Cards - بدون تغییر */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/10 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">کل مهمان‌ها</p>
              <p className="text-2xl font-bold text-white">{stats.total}</p>
            </div>
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/10 rounded-xl p-4 border border-amber-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">مهمان‌های خاص</p>
              <p className="text-2xl font-bold text-white">{stats.special}</p>
            </div>
            <div className="w-12 h-12 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500/20 to-green-600/10 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">ثبت شده امروز</p>
              <p className="text-2xl font-bold text-white">{stats.today}</p>
            </div>
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 rounded-xl p-4 border border-purple-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">درآمد کل</p>
              <p className="text-2xl font-bold text-white">{stats.totalIncome.toLocaleString()}</p>
              <p className="text-xs text-gray-500">تومان</p>
            </div>
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filter - بدون تغییر */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <input
              type="text"
              placeholder="جستجو بر اساس نام، تلفن، شهر یا شماره اتاق..."
              className="input input-bordered w-full bg-base-200/50 pr-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>
        <div className="flex gap-2">
          <button 
            className={`btn ${filterType === 'all' ? 'btn-primary' : 'btn-outline'}`}
            onClick={() => setFilterType('all')}
          >
            همه
          </button>
          <button 
            className={`btn ${filterType === 'special' ? 'btn-warning' : 'btn-outline btn-warning'}`}
            onClick={() => setFilterType('special')}
          >
            مهمان خاص
          </button>
          <button 
            className={`btn ${filterType === 'today' ? 'btn-success' : 'btn-outline btn-success'}`}
            onClick={() => setFilterType('today')}
          >
            امروز
          </button>
        </div>
      </div>

      {/* Table */}
      {loading1 ? (
        <div className="flex justify-center py-20">
          <span className="loading loading-spinner loading-lg text-primary"></span>
        </div>
      ) : (
        <div className="bg-base-300/50 rounded-xl shadow-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table w-full">
              <thead>
                <tr className="bg-base-200/50">
                  <th className="text-right">نام کامل</th>
                  <th className="text-center">اتاق</th>
                  <th className="text-center">شهر</th>
                  <th className="text-center">تلفن</th>
                  <th className="text-center">ورود</th>
                  <th className="text-center">خروج</th>
                  <th className="text-center">اقامت</th>
                  <th className="text-center">قیمت</th>
                  <th className="text-center">وضعیت</th>
                  <th className="text-center">عملیات</th>
                </tr>
              </thead>
              <tbody>
                {filteredGuests.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="text-center py-10">
                      <div className="flex flex-col items-center gap-3">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <p className="text-gray-400">مهمانی یافت نشد</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredGuests.map((guest, index) => (
                    <GuestRow 
                      key={guest._id} 
                      guest={guest} 
                      index={index}
                      onRefresh={() => window.location.reload()}
                    />
                  ))
                )}
                {/* عنصر تشخیص اسکرول */}
                {!loading1 && hasMore && (
                  <tr ref={loaderRef}>
                    <td colSpan="10" className="text-center py-4">
                      {isLoadingMore && (
                        <span className="loading loading-spinner loading-md text-primary"></span>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Results Count */}
      {!loading1 && filteredGuests.length > 0 && (
        <div className="text-center mt-4 text-gray-400">
          نمایش {filteredGuests.length} از {guests.length} مهمان
        </div>
      )}
    </div>
  );
}

// GuestRow بدون تغییر (همان کد قبلی)
function GuestRow({ guest, index, onRefresh }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const navigate = useNavigate();

  const handleDelete = () => {
    if (showDeleteConfirm) {
      DELGuest(guest._id)
        .then(() => onRefresh())
        .catch(err => console.error(err));
    } else {
      setShowDeleteConfirm(true);
      setTimeout(() => setShowDeleteConfirm(false), 3000);
    }
  };

  return (
    <tr className={`hover:bg-base-200/50 transition-colors ${index % 2 === 0 ? 'bg-base-100/30' : ''}`}>
      <td className="text-right">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-primary/30 to-secondary/30 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold">{guest.fullname?.charAt(0) || '-'}</span>
          </div>
          <div>
            <p className="font-medium text-white">{guest.fullname || '-'}</p>
            <p className="text-xs text-gray-400">{new Date(guest.createdAt).toLocaleDateString('fa-IR', options)}</p>
          </div>
        </div>
      </td>
      <td className="text-center">
        <span className="badge badge-lg bg-base-200">{guest.number_room || '-'}</span>
      </td>
      <td className="text-center text-gray-300">{guest.city || '-'}</td>
      <td className="text-center text-gray-300" dir="ltr">{guest.phone || '-'}</td>
      <td className="text-center text-gray-300">{guest.login_date || '-'}</td>
      <td className="text-center text-gray-300">{guest.logout_date || '-'}</td>
      <td className="text-center">
        <span className="badge badge-primary">{guest.stay_time || 0} روز</span>
      </td>
      <td className="text-center">
        <span className="text-white font-medium">{guest.price?.toLocaleString() || 0}</span>
        <span className="text-xs text-gray-400 mr-1">تومان</span>
      </td>
      <td className="text-center">
        {guest.isSpecial ? (
          <span className="badge badge-warning gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
            خاص
          </span>
        ) : (
          <span className="badge badge-ghost">عادی</span>
        )}
      </td>
      <td className="text-center sticky left-0">
        <div className="flex items-center justify-center gap-1">
          <button 
            className="btn btn-ghost btn-sm"
            onClick={() => navigate(`/print/${guest._id}`)}
            title="چاپ"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-info" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
          </button>
          <button 
            className={`btn btn-ghost btn-sm ${showDeleteConfirm ? 'btn-error' : ''}`}
            onClick={handleDelete}
            title={showDeleteConfirm ? "تایید حذف" : "حذف"}
          >
            {showDeleteConfirm ? (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            )}
          </button>
        </div>
      </td>
    </tr>
  );
}

export default AllG;